import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore, useSettingsStore, type SystemLanguage } from '../store';
import { api, type UserExamRestrictions } from '../utils/api';
import {
  TrendingUp, BookOpen, ChevronLeft, ChevronRight,
  Sparkles, BarChart2, X, Volume2, Trophy, Languages,
} from 'lucide-react';
import ScoreTrendChart from '../components/ScoreTrendChart';
import { localApi } from '../utils/local-api';

// ─── helpers ────────────────────────────────────────────────────────────────

const LANGUAGE_OPTIONS: Array<{ value: SystemLanguage; label: string }> = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'id', label: 'Bahasa Indonesia' },
];

const getUiText = (language: SystemLanguage) => {
  if (language === 'en') {
    return {
      pageTitle: 'Stats',
      pageSubtitle: 'Score trend · Vocabulary proficiency distribution · Recommended review',
      systemLanguageTitle: 'System Language',
      systemLanguageDesc: 'Switch the interface language for this app.',
      languageSaved: 'Your choice is saved automatically.',
      unauthorized: 'No access',
      scoreTrend: 'Score Trend',
      distributionTitle: 'Proficiency Distribution',
      seenWords: 'Words Seen',
      needsWork: 'Needs Work',
      mastered: 'Mastered',
      recommendedTitle: 'Recommended Review',
      startReview: 'Start Review ->',
      recommendedHint: 'Sorted by lowest proficiency + longest time since last seen',
      allWeakWords: 'All Weak Vocabulary',
      hskType: 'HSK Type',
      hskLevel: 'HSK Level',
      noVocabRecords: 'No vocabulary records yet. Complete more tests to see weak words here.',
      noVocabForFilter: 'No vocabulary matches the current filters.',
      vocabLoadError: 'Could not load vocabulary. Try again.',
      retry: 'Retry',
      hanzi: 'Hanzi',
      pinyin: 'Pinyin',
      meaning: 'English Meaning',
      level: 'Level',
      proficiency: 'Proficiency',
      appear: 'Seen',
      correctRate: 'Accuracy',
      quizSettings: 'Review Settings',
      part1Title: 'Part 1 · Audio to Hanzi',
      part1Desc: 'Play the audio and choose the correct Hanzi from 4 options.',
      part2Title: 'Part 2 · Pinyin & Meaning',
      part2Desc: 'Look at the Hanzi and choose the pinyin and English meaning.',
      startQuiz: 'Start Review ->',
      quizP1Header: 'Part 1 · Audio to Hanzi',
      quizP2PinyinHeader: 'Part 2 · Choose Pinyin',
      quizP2MeaningHeader: 'Part 2 · Choose Meaning',
      chooseHeard: 'Choose the Hanzi you hear',
      choosePinyin: 'Choose the correct pinyin',
      chooseMeaning: 'Choose the correct English meaning',
      dontKnow: "I don't know",
      correctAnswer: 'Correct answer',
      nextQuestion: 'Next ->',
      correctNextQuestion: '✓ Next ->',
      reviewComplete: 'Review Complete!',
      part2PinyinShort: 'Part 2 · Pinyin',
      part2MeaningShort: 'Part 2 · Meaning',
      updatingVocab: 'Updating vocabulary data...',
      proficiencyUpdated: 'Proficiency updated. Keep going!',
      done: 'Done',
      never: 'Never',
      today: 'Today',
      yesterday: 'Yesterday',
      veryWeak: 'Very weak',
      weak: 'Weak',
      neutral: 'Average',
      good: 'Good',
      proficientTier: 'Proficient',
      previousPage: 'Previous',
      nextPageLabel: 'Next',
      paginationLabel: 'Weak vocabulary pages',
      pageSizeLabel: (size: number) => `${size} words per page`,
      levelBadge: (level: number | string) => `L${level}`,
      totalWordsBadge: (count: number) => `${count} words`,
      recommendedCount: (count: number) => ` · ${count} words`,
      tierCount: (count: number) => `${count}`,
      pageSummary: (page: number, total: number, count: number, start: number, end: number) => `Page ${page} / ${total} · Showing ${start}–${end} of ${count} words`,
      dayAgo: (days: number) => `${days} day${days === 1 ? '' : 's'} ago`,
      rangeCount: (current: number, total: number) => `${current} / ${total}`,
    };
  }

  if (language === 'id') {
    return {
      pageTitle: 'Statistik',
      pageSubtitle: 'Tren nilai · Distribusi kemahiran kosakata · Rekomendasi review',
      systemLanguageTitle: 'Bahasa Sistem',
      systemLanguageDesc: 'Ganti bahasa antarmuka aplikasi.',
      languageSaved: 'Pilihan ini tersimpan otomatis.',
      unauthorized: 'Tidak diizinkan',
      scoreTrend: 'Tren Nilai',
      distributionTitle: 'Distribusi Kemahiran',
      seenWords: 'Kata Dipelajari',
      needsWork: 'Perlu Latihan',
      mastered: 'Sudah Dikuasai',
      recommendedTitle: 'Rekomendasi Review',
      startReview: 'Mulai Review ->',
      recommendedHint: 'Diurutkan berdasarkan kemahiran terendah + paling lama tidak muncul',
      allWeakWords: 'Semua Kosakata Lemah',
      hskType: 'Tipe HSK',
      hskLevel: 'Level HSK',
      noVocabRecords: 'Belum ada catatan kosakata. Selesaikan lebih banyak tes agar kosakata lemah muncul di sini.',
      noVocabForFilter: 'Tidak ada kosakata yang cocok dengan filter saat ini.',
      vocabLoadError: 'Kosakata gagal dimuat. Coba lagi.',
      retry: 'Coba lagi',
      hanzi: 'Hanzi',
      pinyin: 'Pinyin',
      meaning: 'Arti Indonesia',
      level: 'Level',
      proficiency: 'Kemahiran',
      appear: 'Muncul',
      correctRate: 'Akurasi',
      quizSettings: 'Pengaturan Review',
      part1Title: 'Part 1 · Audio ke Hanzi',
      part1Desc: 'Putar audio lalu pilih Hanzi yang benar dari 4 opsi.',
      part2Title: 'Part 2 · Pinyin & Arti',
      part2Desc: 'Lihat Hanzi lalu pilih pinyin dan arti Indonesia.',
      startQuiz: 'Mulai Review ->',
      quizP1Header: 'Part 1 · Audio ke Hanzi',
      quizP2PinyinHeader: 'Part 2 · Pilih Pinyin',
      quizP2MeaningHeader: 'Part 2 · Pilih Arti',
      chooseHeard: 'Pilih Hanzi yang kamu dengar',
      choosePinyin: 'Pilih pinyin yang benar',
      chooseMeaning: 'Pilih arti Indonesia yang benar',
      dontKnow: 'Tidak tahu',
      correctAnswer: 'Jawaban benar',
      nextQuestion: 'Berikutnya ->',
      correctNextQuestion: '✓ Berikutnya ->',
      reviewComplete: 'Review Selesai!',
      part2PinyinShort: 'Part 2 · Pinyin',
      part2MeaningShort: 'Part 2 · Arti',
      updatingVocab: 'Memperbarui data kosakata...',
      proficiencyUpdated: 'Kemahiran diperbarui. Tetap semangat!',
      done: 'Selesai',
      never: 'Belum pernah',
      today: 'Hari ini',
      yesterday: 'Kemarin',
      veryWeak: 'Sangat lemah',
      weak: 'Lemah',
      neutral: 'Biasa',
      good: 'Baik',
      proficientTier: 'Mahir',
      previousPage: 'Sebelumnya',
      nextPageLabel: 'Berikutnya',
      paginationLabel: 'Halaman kosakata lemah',
      pageSizeLabel: (size: number) => `${size} kata per halaman`,
      levelBadge: (level: number | string) => `Lv ${level}`,
      totalWordsBadge: (count: number) => `${count} kata`,
      recommendedCount: (count: number) => ` · ${count} kata`,
      tierCount: (count: number) => `${count} kata`,
      pageSummary: (page: number, total: number, count: number, start: number, end: number) => `Halaman ${page} / ${total} · Menampilkan ${start}–${end} dari ${count} kata`,
      dayAgo: (days: number) => `${days} hari lalu`,
      rangeCount: (current: number, total: number) => `${current} / ${total}`,
    };
  }

  return {
    pageTitle: 'Stats',
    pageSubtitle: '成绩趋势 · 词汇熟练度分布 · 推荐复习',
    systemLanguageTitle: '系统语言',
    systemLanguageDesc: '切换应用界面显示语言。',
    languageSaved: '当前设置会自动保存。',
    unauthorized: '未授权',
    scoreTrend: '成绩趋势',
    distributionTitle: '熟练度分布',
    seenWords: '已接触词汇',
    needsWork: '需要加强',
    mastered: '已掌握',
    recommendedTitle: '推荐复习',
    startReview: '开始复习 ->',
    recommendedHint: '按熟练度最低 + 最久未出现排序',
    allWeakWords: '所有薄弱词汇',
    hskType: 'HSK 类型',
    hskLevel: 'HSK 等级',
    noVocabRecords: '暂无词汇记录，完成更多测试后此处将显示薄弱词汇。',
    noVocabForFilter: '当前筛选条件下没有词汇。',
    vocabLoadError: '词汇加载失败，请重试。',
    retry: '重试',
    hanzi: '汉字',
    pinyin: '拼音',
    meaning: '英文意思',
    level: '等级',
    proficiency: '熟练度',
    appear: '出现',
    correctRate: '正确率',
    quizSettings: '复习设置',
    part1Title: 'Part 1 · 听音选字',
    part1Desc: '播放读音，从 4 个汉字中选正确答案',
    part2Title: 'Part 2 · 选拼音 & 意思',
    part2Desc: '看汉字，分别选拼音和英文意思',
    startQuiz: '开始复习 ->',
    quizP1Header: 'Part 1 · 听音选字',
    quizP2PinyinHeader: 'Part 2 · 选拼音',
    quizP2MeaningHeader: 'Part 2 · 选意思',
    chooseHeard: '选出你听到的汉字',
    choosePinyin: '选出正确拼音',
    chooseMeaning: '选出正确英文意思',
    dontKnow: '不知道',
    correctAnswer: '正确答案',
    nextQuestion: '下一题 ->',
    correctNextQuestion: '✓ 下一题 ->',
    reviewComplete: '复习完成！',
    part2PinyinShort: 'Part 2 · 拼音',
    part2MeaningShort: 'Part 2 · 意思',
    updatingVocab: '正在更新词汇数据...',
    proficiencyUpdated: '熟练度已更新，继续加油！',
    done: '完成',
    never: '从未',
    today: '今天',
    yesterday: '昨天',
    veryWeak: '非常弱',
    weak: '弱',
    neutral: '一般',
    good: '良好',
    proficientTier: '熟练',
    previousPage: '上一页',
    nextPageLabel: '下一页',
    paginationLabel: '薄弱词汇分页',
    pageSizeLabel: (size: number) => `每页 ${size} 词`,
    levelBadge: (level: number | string) => `${level}级`,
    totalWordsBadge: (count: number) => `${count} 词`,
    recommendedCount: (count: number) => `，共 ${count} 词`,
    tierCount: (count: number) => `${count} 词`,
    pageSummary: (page: number, total: number, count: number, start: number, end: number) => `第 ${page} / ${total} 页 · 显示第 ${start}–${end} 词，共 ${count} 词`,
    dayAgo: (days: number) => `${days} 天前`,
    rangeCount: (current: number, total: number) => `第 ${current} / ${total}`,
  };
};

const normalizeHskLevelValue = (value?: string | number | null) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > 0) return String(Math.trunc(numeric));
  return raw.replace(/\.0$/, '');
};
const formatHskLevelLabel = (v?: string | number | null) => { const n = normalizeHskLevelValue(v); return n ? `HSK${n}` : 'HSK'; };
const CSCA_LEVEL_NAMES: Record<string, string> = { '1': '中文文科', '2': '中文理科', '3': '数学', '4': '物理', '5': '化学' };
const vocabLevelLabel = (version: string | null, level: string | number) => version === 'CSCA' ? (CSCA_LEVEL_NAMES[String(level)] || `CSCA ${level}`) : formatHskLevelLabel(level);

const getHistoryFilterType = (part?: string | null, testId?: string | null) => {
  const p = String(part  || '').trim().toLowerCase();
  const t = String(testId || '').trim().toLowerCase();
  if (t.startsWith('practice-') || p.startsWith('practice-')) return 'review';
  if (p === 'listening')  return 'listening';
  if (p === 'reading')    return 'reading';
  if (p === 'writing')    return 'writing';
  if (p === 'simulation') return 'simulation';
  return p || 'other';
};

const getHistoryDisplayName = (language: SystemLanguage, bv: string, level: number | string, part: string, count: number, testId?: string | null) => {
  const p = String(part  || '').trim().toLowerCase();
  const t = String(testId || '').trim().toLowerCase();
  const l = normalizeHskLevelValue(level);
  if (bv === 'CSCA') return `CSCA ${CSCA_LEVEL_NAMES[l] || l} · ${part} ${count}`;
  if (language === 'en') {
    if (t.startsWith('practice-listening-') || t === 'practice-listening') return `HSK ${bv} L${l} Listening Review ${count}`;
    if (t.startsWith('practice-reading-')   || t === 'practice-reading')   return `HSK ${bv} L${l} Reading Review ${count}`;
    if (t.startsWith('practice-writing-')   || t === 'practice-writing')   return `HSK ${bv} L${l} Writing Review ${count}`;
    if (p === 'listening')  return `HSK ${bv} L${l} Listening Mock ${count}`;
    if (p === 'reading')    return `HSK ${bv} L${l} Reading Mock ${count}`;
    if (p === 'writing')    return `HSK ${bv} L${l} Writing Mock ${count}`;
    if (p === 'simulation') return `HSK ${bv} L${l} Simulation ${count}`;
    return `HSK ${bv} L${l} ${part} ${count}`;
  }
  if (language === 'id') {
    if (t.startsWith('practice-listening-') || t === 'practice-listening') return `HSK ${bv} Lv ${l} Review Mendengar ${count}`;
    if (t.startsWith('practice-reading-')   || t === 'practice-reading')   return `HSK ${bv} Lv ${l} Review Membaca ${count}`;
    if (t.startsWith('practice-writing-')   || t === 'practice-writing')   return `HSK ${bv} Lv ${l} Review Menulis ${count}`;
    if (p === 'listening')  return `HSK ${bv} Lv ${l} Simulasi Mendengar ${count}`;
    if (p === 'reading')    return `HSK ${bv} Lv ${l} Simulasi Membaca ${count}`;
    if (p === 'writing')    return `HSK ${bv} Lv ${l} Simulasi Menulis ${count}`;
    if (p === 'simulation') return `HSK ${bv} Lv ${l} Simulasi ${count}`;
    return `HSK ${bv} Lv ${l} ${part} ${count}`;
  }
  if (t.startsWith('practice-listening-') || t === 'practice-listening') return `HSK ${bv} ${l}级 复习 听力 ${count}`;
  if (t.startsWith('practice-reading-')   || t === 'practice-reading')   return `HSK ${bv} ${l}级 复习 阅读 ${count}`;
  if (t.startsWith('practice-writing-')   || t === 'practice-writing')   return `HSK ${bv} ${l}级 复习 写作 ${count}`;
  if (p === 'listening')  return `HSK ${bv} ${l}级 听力模拟 ${count}`;
  if (p === 'reading')    return `HSK ${bv} ${l}级 阅读模拟 ${count}`;
  if (p === 'writing')    return `HSK ${bv} ${l}级 写作模拟 ${count}`;
  if (p === 'simulation') return `HSK ${bv} ${l}级 模拟 ${count}`;
  return `HSK ${bv} ${l}级 ${part} ${count}`;
};

const getProficiencyBadge = (p: number) => {
  if (p <= -5) return { bg: 'bg-red-100',    text: 'text-red-700'    };
  if (p <= 10) return { bg: 'bg-orange-100', text: 'text-orange-700' };
  return { bg: 'bg-green-100', text: 'text-green-700' };
};

const daysAgo = (language: SystemLanguage, dateStr?: string | null) => {
  const text = getUiText(language);
  if (!dateStr) return text.never;
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return text.today;
  if (days === 1) return text.yesterday;
  return text.dayAgo(days);
};

// ─── config ─────────────────────────────────────────────────────────────────

type DistKey = 'weak' | 'mastered';
const getTiers = (language: SystemLanguage): Array<{ key: DistKey; label: string; bar: string; text: string }> => {
  const ui = getUiText(language);
  return [
    { key: 'weak', label: `${ui.needsWork} (≤10)`, bar: 'bg-orange-400', text: 'text-orange-700' },
    { key: 'mastered', label: `${ui.mastered} (>10)`, bar: 'bg-green-400', text: 'text-green-700' },
  ];
};

const getMeaning = (item: { En_meaning?: string; Id_meaning?: string }, language: SystemLanguage) => (
  language === 'id'
    ? (item.Id_meaning || item.En_meaning || '')
    : (item.En_meaning || item.Id_meaning || '')
);

type SortCol = 'proficiency' | 'appear' | 'correct_rate';
type SortDir = 'asc' | 'desc';
const SortIcon = ({ active, dir }: { active: boolean; dir: SortDir }) => (
  <span className={`ml-1 text-[11px] ${active ? 'text-[#4F46E5]' : 'text-gray-300'}`}>
    {!active ? '↕' : dir === 'asc' ? '↑' : '↓'}
  </span>
);

// ─── quiz types ──────────────────────────────────────────────────────────────

type QuizMode = 'settings' | 'quiz_p1' | 'quiz_p2_pinyin' | 'quiz_p2_meaning' | 'result';
type WordResult = {
  p1?:  'correct' | 'wrong' | 'dont_know';
  p2p?: 'correct' | 'wrong';
  p2m?: 'correct' | 'wrong';
};

// ─── audio URL helper (module-level, no closure issues) ──────────────────────

const TONE_MAP: Record<string, string> = {
  'ā':'a','á':'a','ǎ':'a','à':'a',
  'ē':'e','é':'e','ě':'e','è':'e',
  'ī':'i','í':'i','ǐ':'i','ì':'i',
  'ō':'o','ó':'o','ǒ':'o','ò':'o',
  'ū':'u','ú':'u','ǔ':'u','ù':'u',
  'ü':'v','ǘ':'v','ǚ':'v','ǜ':'v',
  'ń':'n','ň':'n','ǹ':'n',
  'Ā':'A','Á':'A','Ǎ':'A','À':'A',
  'Ē':'E','É':'E','Ě':'E','È':'E',
  'Ī':'I','Í':'I','Ǐ':'I','Ì':'I',
  'Ō':'O','Ó':'O','Ǒ':'O','Ò':'O',
  'Ū':'U','Ú':'U','Ǔ':'U','Ù':'U',
  'Ü':'V','Ǘ':'V','Ǚ':'V','Ǜ':'V',
};

function buildVocabAudioUrl(item: { pinyin?: string; version?: string; level?: number; audio?: string }): string {
  if (item.version === 'CSCA' && item.audio) return `${BACKEND_URL}/media/${item.audio.split('/').map(encodeURIComponent).join('/')}`;
  const v = item.version === '2' ? 'HSK2' : 'HSK3';
  const l = item.level || 1;
  const pinyin = item.pinyin || '';
  const normalized = pinyin.split('').map(c => TONE_MAP[c] || c).join('').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  return `${BACKEND_URL}/media/Vocab ${v}/Audio/HSK${l}/${encodeURIComponent(normalized)}.mp3`;
}

const PAGE_SIZE = 20;

// ─── component ───────────────────────────────────────────────────────────────

type MyVocabProps = {
  embedded?: boolean;
  initialRestrictions?: UserExamRestrictions;
  visibleVersions?: Array<'2.0' | '3.0' | 'CSCA'>;
  readOnlyPreview?: boolean;
};

const MyVocab: React.FC<MyVocabProps> = ({ embedded = false, initialRestrictions, visibleVersions, readOnlyPreview = false }) => {
  const { token } = useAuthStore();
  const { systemLanguage, setSystemLanguage } = useSettingsStore();
  const pageSize = embedded ? 5 : PAGE_SIZE;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const vocabSectionRef = useRef<HTMLDivElement | null>(null);
  const uiText = useMemo(() => getUiText(systemLanguage), [systemLanguage]);
  const tiers = useMemo(() => getTiers(systemLanguage), [systemLanguage]);

  // score trend
  const [serverHistory, setServerHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // distribution + recommended
  type Distribution = { weak: number; mastered: number; total: number };
  const [distribution, setDistribution] = useState<Distribution | null>(null);
  const [recommended, setRecommended] = useState<any[]>([]);
  const [loadingVocabStats, setLoadingVocabStats] = useState(true);
  const statsRequestIdRef = useRef(0);

  // user restrictions (allowed HSK levels)
  type Restrictions = { hsk2Levels: number[]; hsk3Levels: number[]; cscaLevels: number[]; hsk2Restricted: boolean; hsk3Restricted: boolean; cscaRestricted: boolean };
  const [restrictions, setRestrictions] = useState<Restrictions | null>(initialRestrictions ?? null);
  const [activeVersion, setActiveVersion] = useState<'2.0' | '3.0' | 'CSCA' | null>(null);

  // vocab table
  const [vocab, setVocab] = useState<any[]>([]);
  const [totalVocab, setTotalVocab] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasProficiencyData, setHasProficiencyData] = useState<boolean | null>(null);
  const [loadingVocab, setLoadingVocab] = useState(true);
  const [vocabError, setVocabError] = useState(false);
  const vocabRequestIdRef = useRef(0);
  const [filterLevels, setFilterLevels] = useState<string[]>([]);
  const [sortCol, setSortCol] = useState<SortCol>('proficiency');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // review quiz state
  const [reviewActive, setReviewActive]     = useState(false);
  const [quizMode, setQuizMode]             = useState<QuizMode>('settings');
  const [quizSettings, setQuizSettings]     = useState({ part1: true, part2: true });
  const [quizIdx, setQuizIdx]               = useState(0);
  const [quizOptions, setQuizOptions]       = useState<string[]>([]);
  const [quizSelected, setQuizSelected]     = useState<number | null>(null); // null=unanswered, -1=dont know
  const [quizIsCorrect, setQuizIsCorrect]   = useState<boolean | null>(null);
  const [wordResults, setWordResults]       = useState<WordResult[]>([]);
  const [submittingReview, setSubmittingReview] = useState(false);
  const defaultLevelFilter = (version: '2.0' | '3.0' | 'CSCA', scope: Restrictions | null): string[] => {
    if (!scope) return [];
    const levels = version === 'CSCA' ? scope.cscaLevels : version === '3.0' ? scope.hsk3Levels : scope.hsk2Levels;
    if (!levels.length) return [];
    return [String(version === 'CSCA' && levels.includes(3) ? 3 : Math.max(...levels))];
  };

  // ── load history ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || embedded) return;
    (async () => {
      try {
        const res = await localApi.getHistory(token);
        const counter: Record<string, number> = {};
        const asc = (Array.isArray(res) ? res : []).sort((a: any, b: any) =>
          new Date(a.date_taken || a.date).getTime() - new Date(b.date_taken || b.date).getTime()
        );
        const mapped = asc.map((t: any) => {
          const part = String(t.part || '');
          const bv   = t.bank_version || '2.0';
          const key  = `${bv}-${t.level}-${part}`;
          const cnt  = (counter[key] ?? 0) + 1;
          counter[key] = cnt;
          const ws  = t.weighted_score ?? t.score;
          const ms  = t.max_score ?? 100;
          return {
            id: t.id, name: getHistoryDisplayName(systemLanguage, bv, t.level, part, cnt, t.id),
            level: t.level, part: t.part, filterType: getHistoryFilterType(part, t.id),
            bankVersion: bv, completedAt: t.date_taken || t.date,
            percentage: ms ? Math.round((ws / ms) * 100) : 0, isReviewRecord: false,
          };
        });
        setServerHistory([...mapped].reverse());
      } catch { /* silent */ }
      finally { setLoadingHistory(false); }
    })();
  }, [systemLanguage, token, embedded]);

  // ── load distribution + recommended ─────────────────────────────────────
  const loadVocabStats = async () => {
    if (!token || !activeVersion) return;
    const requestId = ++statsRequestIdRef.current;
    try {
      const res = await api.getUserVocabStats(token, activeVersion, embedded);
      if (requestId === statsRequestIdRef.current) {
        setDistribution(res.distribution);
        setRecommended(res.recommended || []);
      }
    } catch { /* silent */ }
    finally { if (requestId === statsRequestIdRef.current) setLoadingVocabStats(false); }
  };
  useEffect(() => { if (activeVersion) { setLoadingVocabStats(true); setDistribution(null); setRecommended([]); void loadVocabStats(); } }, [token, activeVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── load user restrictions ────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    if (embedded) {
      const res = initialRestrictions;
      setRestrictions(res ?? null);
      if (!res) { setActiveVersion(null); return; }
      const permitted = [
        ...(!res.hsk2Restricted && res.hsk2Levels.length ? ['2.0' as const] : []),
        ...(!res.hsk3Restricted && res.hsk3Levels.length ? ['3.0' as const] : []),
        ...(!res.cscaRestricted && res.cscaLevels.length ? ['CSCA' as const] : []),
      ].filter(version => !visibleVersions || visibleVersions.includes(version));
      const first = permitted[0] ?? null;
      setActiveVersion(current => current && permitted.includes(current) ? current : first);
      if (first) setFilterLevels(defaultLevelFilter(first, res));
      return;
    }
    api.getUserRestrictions(token)
      .then(res => {
        setRestrictions(res);
        const available = [
          ...(!res.hsk2Restricted && res.hsk2Levels.length ? ['2.0' as const] : []),
          ...(!res.hsk3Restricted && res.hsk3Levels.length ? ['3.0' as const] : []),
          ...(!res.cscaRestricted && res.cscaLevels.length ? ['CSCA' as const] : []),
        ];
        setActiveVersion(current => current && available.includes(current) ? current : available[0] ?? null);
      })
      .catch(() => { setRestrictions(null); setActiveVersion(null); });
  }, [token, embedded, initialRestrictions, visibleVersions]);

  const availableVersions = (restrictions ? [
    ...(!restrictions.hsk2Restricted && restrictions.hsk2Levels.length > 0 ? ['2.0' as const] : []),
    ...(!restrictions.hsk3Restricted && restrictions.hsk3Levels.length > 0 ? ['3.0' as const] : []),
    ...(!restrictions.cscaRestricted && restrictions.cscaLevels.length > 0 ? ['CSCA' as const] : []),
  ] : []).filter(version => !embedded || !visibleVersions || visibleVersions.includes(version));
  const availableLevels = restrictions
    ? (activeVersion === 'CSCA' ? restrictions.cscaLevels : activeVersion === '3.0' ? restrictions.hsk3Levels : restrictions.hsk2Levels).map(String)
    : [];

  // ── load vocab table (server-side sort) ──────────────────────────────────
  const loadVocab = async (page: number) => {
    if (!token || !activeVersion) return;
    const requestId = ++vocabRequestIdRef.current;
    setLoadingVocab(true);
    setVocabError(false);
    setCurrentPage(page);
    try {
      const res = await api.getUserVocab(token, activeVersion, page, pageSize, filterLevels, sortCol, sortDir);
      if (requestId !== vocabRequestIdRef.current) return;
      setVocab((res.vocab || []).slice(0, pageSize));
      setTotalVocab(res.total || 0);
      setTotalPages(res.totalPages || 1);
      setHasProficiencyData(res.hasProficiencyData !== false);
    } catch {
      if (requestId === vocabRequestIdRef.current) setVocabError(true);
    } finally {
      if (requestId === vocabRequestIdRef.current) setLoadingVocab(false);
    }
  };
  const changeVocabPage = (page: number) => {
    if (loadingVocab || page < 1 || page > totalPages) return;
    void loadVocab(page);
    vocabSectionRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };
  // Reload when filters OR sort changes
  useEffect(() => { if (activeVersion) { setCurrentPage(1); void loadVocab(1); } }, [token, activeVersion, filterLevels, sortCol, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── derived ──────────────────────────────────────────────────────────────
  const trendData = useMemo(() =>
    serverHistory.filter(h => h.bankVersion === activeVersion && !h.isReviewRecord && h.percentage !== null).map(h => ({
      completedAt: h.completedAt, percentage: h.percentage as number, part: activeVersion === 'CSCA' ? 'simulation' : h.part, name: h.name,
    })), [serverHistory, activeVersion]);

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const maxTierCount = distribution
    ? Math.max(distribution.weak, distribution.mastered, 1)
    : 1;

  const needsWork  = distribution?.weak ?? 0;
  const mastered   = distribution?.mastered ?? 0;
  const totalWords = distribution?.total ?? 0;

  // ── quiz helpers ─────────────────────────────────────────────────────────

  const playVocabAudio = useCallback((item: { pinyin?: string; version?: string; level?: number; audio?: string }) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const audio = new Audio(buildVocabAudioUrl(item));
    audioRef.current = audio;
    audio.play().catch(() => {});
  }, []);

  const prepareOptions = useCallback((idx: number, type: 'hanzi' | 'pinyin' | 'meaning', list: any[]) => {
    if (!list[idx]) return;
    const correct    = list[idx];
    const distractors = list.filter((_, i) => i !== idx).sort(() => Math.random() - 0.5).slice(0, 3);
    const pool       = [correct, ...distractors].sort(() => Math.random() - 0.5);
    if (type === 'hanzi')   setQuizOptions(pool.map(v => v.hanzi));
    else if (type === 'pinyin')   setQuizOptions(pool.map(v => v.pinyin));
    else                          setQuizOptions(pool.map(v => getMeaning(v, systemLanguage)));
    setQuizSelected(null);
    setQuizIsCorrect(null);
  }, [systemLanguage]);

  const startReview = () => {
    setWordResults(recommended.map(() => ({})));
    setQuizIdx(0);
    setQuizMode('settings');
    setReviewActive(true);
  };

  const closeReview = () => {
    setReviewActive(false);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  };

  const startQuiz = () => {
    const idx = 0;
    setQuizIdx(idx);
    if (quizSettings.part1) {
      setQuizMode('quiz_p1');
      prepareOptions(idx, 'hanzi', recommended);
      setTimeout(() => playVocabAudio(recommended[idx]), 80);
    } else {
      setQuizMode('quiz_p2_pinyin');
      prepareOptions(idx, 'pinyin', recommended);
    }
  };

  const handleAnswer = (optionIdx: number) => {
    if (quizSelected !== null) return;
    const word = recommended[quizIdx];
    const isDontKnow = optionIdx === -1;

    let isCorrectAnswer = false;
    if (!isDontKnow) {
      const sel = quizOptions[optionIdx];
      if (quizMode === 'quiz_p1')          isCorrectAnswer = sel === word.hanzi;
      else if (quizMode === 'quiz_p2_pinyin')  isCorrectAnswer = sel === word.pinyin;
      else                                      isCorrectAnswer = sel === getMeaning(word, systemLanguage);
    }

    setQuizSelected(optionIdx);
    setQuizIsCorrect(!isDontKnow && isCorrectAnswer);

    const outcome: 'correct' | 'wrong' | 'dont_know' = isDontKnow ? 'dont_know' : isCorrectAnswer ? 'correct' : 'wrong';
    const updated = [...wordResults];
    if      (quizMode === 'quiz_p1')          updated[quizIdx] = { ...updated[quizIdx], p1:  outcome };
    else if (quizMode === 'quiz_p2_pinyin')   updated[quizIdx] = { ...updated[quizIdx], p2p: outcome === 'dont_know' ? 'wrong' : outcome as 'correct'|'wrong' };
    else                                      updated[quizIdx] = { ...updated[quizIdx], p2m: outcome === 'dont_know' ? 'wrong' : outcome as 'correct'|'wrong' };
    setWordResults(updated);

    if (!isDontKnow && isCorrectAnswer) {
      new Audio(`${BACKEND_URL}/media/hsk-media/Audio/correct-answer.mp3`).play().catch(() => {});
    }
  };

  // Called from "下一题 →" button — reads fresh state at click time (no stale closure)
  const handleQuizNext = () => {
    setQuizSelected(null);
    setQuizIsCorrect(null);

    if (quizMode === 'quiz_p1') {
      if (quizIdx < recommended.length - 1) {
        const next = quizIdx + 1;
        setQuizIdx(next);
        prepareOptions(next, 'hanzi', recommended);
        setTimeout(() => playVocabAudio(recommended[next]), 80);
      } else if (quizSettings.part2) {
        setQuizIdx(0);
        setQuizMode('quiz_p2_pinyin');
        prepareOptions(0, 'pinyin', recommended);
      } else {
        handleFinishQuiz();
      }
    } else if (quizMode === 'quiz_p2_pinyin') {
      setQuizMode('quiz_p2_meaning');
      prepareOptions(quizIdx, 'meaning', recommended);
    } else { // quiz_p2_meaning
      if (quizIdx < recommended.length - 1) {
        const next = quizIdx + 1;
        setQuizIdx(next);
        setQuizMode('quiz_p2_pinyin');
        prepareOptions(next, 'pinyin', recommended);
      } else {
        handleFinishQuiz();
      }
    }
  };

  const handleFinishQuiz = async () => {
    setQuizMode('result');
    if (token && !readOnlyPreview) {
      setSubmittingReview(true);
      try {
        const items = recommended.map((item, i) => {
          const r = wordResults[i] || {};
          let outcome: 'correct' | 'wrong' | 'dont_know' = 'correct';
          if (quizSettings.part1) {
            if (r.p1 === 'dont_know')  outcome = 'dont_know';
            else if (r.p1 === 'wrong') outcome = 'wrong';
          }
          if (outcome === 'correct' && quizSettings.part2) {
            if (r.p2p === 'wrong' || r.p2m === 'wrong') outcome = 'wrong';
          }
          return { vocab_id: item.vocab_id, outcome };
        });
        if (!activeVersion) return;
        await api.updateVocabReview(items, token, activeVersion);
        await Promise.all([loadVocabStats(), loadVocab(1)]);
      } catch { /* silent */ }
      setSubmittingReview(false);
    }
  };

  // ─── render ───────────────────────────────────────────────────────────────

  const currentWord = recommended[quizIdx];
  const currentMeaning = currentWord ? getMeaning(currentWord, systemLanguage) : '';
  const renderVocabPagination = (position: 'top' | 'bottom') => (
    <nav aria-label={uiText.paginationLabel} className={`flex flex-wrap items-center justify-between gap-3 ${position === 'top' ? 'mb-4' : 'mt-5 pt-4 border-t border-gray-100'}`}>
      <p aria-live="polite" className="text-sm text-gray-600">
        {uiText.pageSummary(currentPage, totalPages, totalVocab, (currentPage - 1) * pageSize + 1, Math.min(currentPage * pageSize, totalVocab))}
      </p>
      {totalPages > 1 && (
        <div className="flex gap-2">
          <button type="button" onClick={() => changeVocabPage(currentPage - 1)} disabled={currentPage <= 1 || loadingVocab}
            className="flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed">
            <ChevronLeft aria-hidden="true" className="w-4 h-4" />{uiText.previousPage}
          </button>
          <button type="button" onClick={() => changeVocabPage(currentPage + 1)} disabled={currentPage >= totalPages || loadingVocab}
            className="flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed">
            {uiText.nextPageLabel}<ChevronRight aria-hidden="true" className="w-4 h-4" />
          </button>
        </div>
      )}
    </nav>
  );

  if (embedded && restrictions && availableVersions.length === 0) {
    return <section className="rounded-2xl border border-slate-200 bg-white p-8 text-slate-500">
      {systemLanguage === 'zh' ? '当前没有开放的词汇体系。' : systemLanguage === 'id' ? 'Belum ada kelompok kosakata yang tersedia.' : 'No vocabulary curriculum is available.'}
    </section>;
  }

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-8 max-w-5xl mx-auto'}>

      {/* ── Quiz Review Modal ─────────────────────────────────────────────── */}
      {reviewActive && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">

          {/* Settings screen */}
          {quizMode === 'settings' && (
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#312E81]">{uiText.quizSettings}</h2>
                <button onClick={closeReview} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 p-4 border rounded-xl bg-gray-50 cursor-pointer hover:bg-indigo-50 transition-colors">
                  <input type="checkbox" checked={quizSettings.part1}
                    onChange={e => setQuizSettings(s => ({ ...s, part1: e.target.checked }))}
                    className="w-5 h-5 accent-[#4F46E5] rounded" />
                  <div>
                    <div className="font-bold text-sm text-[#312E81]">{uiText.part1Title}</div>
                    <div className="text-xs text-gray-500">{uiText.part1Desc}</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-4 border rounded-xl bg-gray-50 cursor-pointer hover:bg-indigo-50 transition-colors">
                  <input type="checkbox" checked={quizSettings.part2}
                    onChange={e => setQuizSettings(s => ({ ...s, part2: e.target.checked }))}
                    className="w-5 h-5 accent-[#4F46E5] rounded" />
                  <div>
                    <div className="font-bold text-sm text-[#312E81]">{uiText.part2Title}</div>
                    <div className="text-xs text-gray-500">{uiText.part2Desc}</div>
                  </div>
                </label>
              </div>

              <button
                onClick={startQuiz}
                disabled={!quizSettings.part1 && !quizSettings.part2}
                className="w-full py-3 bg-[#4F46E5] text-white rounded-2xl font-bold hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50"
              >
                {uiText.startQuiz}
              </button>
            </div>
          )}

          {/* Quiz screens */}
          {(quizMode === 'quiz_p1' || quizMode === 'quiz_p2_pinyin' || quizMode === 'quiz_p2_meaning') && currentWord && (
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-gray-400">
                  {quizMode === 'quiz_p1' ? uiText.quizP1Header : quizMode === 'quiz_p2_pinyin' ? uiText.quizP2PinyinHeader : uiText.quizP2MeaningHeader}
                  <span className="ml-2">{uiText.rangeCount(quizIdx + 1, recommended.length)}</span>
                </div>
                <button onClick={closeReview} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div className="bg-[#4F46E5] h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${((quizIdx + 1) / recommended.length) * 100}%` }} />
              </div>

              {/* Question area */}
              {quizMode === 'quiz_p1' ? (
                <div className="flex flex-col items-center gap-3 py-2">
                  <p className="text-sm text-gray-500 font-medium">{uiText.chooseHeard}</p>
                  <button
                    onClick={() => playVocabAudio(currentWord)}
                    className="w-20 h-20 rounded-2xl bg-[#4F46E5] text-white flex items-center justify-center shadow-xl active:scale-95 transition-transform"
                  >
                    <Volume2 className="w-10 h-10" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1.5 py-2">
                  <p className="text-xs text-gray-500 font-medium">
                    {quizMode === 'quiz_p2_pinyin' ? uiText.choosePinyin : uiText.chooseMeaning}
                  </p>
                  <div className="text-5xl font-bold text-[#312E81]">{currentWord.hanzi}</div>
                </div>
              )}

              {/* Options */}
              <div className="space-y-2">
                {quizOptions.map((opt, idx) => {
                  let cls = 'bg-white border-2 border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50';
                  if (quizSelected !== null) {
                    const isCorrectOpt =
                      quizMode === 'quiz_p1'          ? opt === currentWord.hanzi :
                      quizMode === 'quiz_p2_pinyin'   ? opt === currentWord.pinyin :
                                                        opt === currentMeaning;
                    const wasSelected = quizSelected >= 0 && quizSelected === idx;
                    if (isCorrectOpt)    cls = 'bg-green-100 border-2 border-green-500 text-green-800 ring-2 ring-green-500';
                    else if (wasSelected) cls = 'bg-red-100 border-2 border-red-500 text-red-800 ring-2 ring-red-500';
                    else                  cls = 'bg-white border-2 border-gray-100 text-gray-400 opacity-50';
                  }
                  return (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(idx)}
                      disabled={quizSelected !== null}
                      className={`w-full p-3 rounded-xl font-bold text-sm transition-all ${cls}`}
                      style={quizMode === 'quiz_p2_pinyin' ? { fontFamily: '"Times New Roman", Times, serif' } : undefined}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>

              {/* Don't know / Next row */}
              {quizSelected === null ? (
                <button
                  onClick={() => handleAnswer(-1)}
                  className="w-full py-2.5 rounded-xl text-purple-500 bg-purple-50 hover:bg-purple-100 font-semibold text-sm transition-colors"
                >
                  {uiText.dontKnow}
                </button>
              ) : (
                <div className="flex items-start gap-2">
                  {(quizSelected === -1 || !quizIsCorrect) && (
                    <div className="flex-1 p-3 bg-amber-50 rounded-xl border border-amber-200 text-sm min-w-0">
                      <div className="text-[10px] font-bold text-amber-600 mb-0.5">{uiText.correctAnswer}</div>
                      <div className="font-bold text-[#312E81] truncate">
                        {quizMode === 'quiz_p1'        ? currentWord.hanzi :
                         quizMode === 'quiz_p2_pinyin' ? currentWord.pinyin :
                                                         currentMeaning}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={handleQuizNext}
                    className="shrink-0 px-5 py-2.5 bg-[#4F46E5] text-white rounded-xl font-bold text-sm hover:bg-indigo-700 active:scale-95 transition-all"
                  >
                    {quizIsCorrect ? uiText.correctNextQuestion : uiText.nextQuestion}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Result screen */}
          {quizMode === 'result' && (
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center gap-5">
              <Trophy className="w-14 h-14 text-yellow-500" />
              <h3 className="text-xl font-bold text-[#312E81]">{uiText.reviewComplete}</h3>

              <div className="w-full space-y-2">
                {quizSettings.part1 && (
                  <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-indigo-50">
                    <span className="font-semibold text-[#312E81] text-sm">{uiText.part1Title}</span>
                    <span className="font-bold text-[#4F46E5]">
                      {wordResults.filter(r => r.p1 === 'correct').length} / {recommended.length}
                    </span>
                  </div>
                )}
                {quizSettings.part2 && (
                  <>
                    <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-indigo-50">
                      <span className="font-semibold text-[#312E81] text-sm">{uiText.part2PinyinShort}</span>
                      <span className="font-bold text-[#4F46E5]">
                        {wordResults.filter(r => r.p2p === 'correct').length} / {recommended.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-indigo-50">
                      <span className="font-semibold text-[#312E81] text-sm">{uiText.part2MeaningShort}</span>
                      <span className="font-bold text-[#4F46E5]">
                        {wordResults.filter(r => r.p2m === 'correct').length} / {recommended.length}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {submittingReview ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4F46E5]" />
                  {uiText.updatingVocab}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center">{readOnlyPreview
                  ? (systemLanguage === 'zh' ? '测试网站不保存复习结果。' : systemLanguage === 'id' ? 'Hasil latihan tidak disimpan di situs uji.' : 'Review results are not saved on the test site.')
                  : uiText.proficiencyUpdated}</p>
              )}

              <button
                onClick={closeReview}
                disabled={submittingReview}
                className="w-full py-3 bg-[#4F46E5] text-white rounded-2xl font-bold hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50"
              >
                {uiText.done}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div className={embedded ? 'space-y-3' : 'text-center space-y-2'}>
        {embedded
          ? <><h2 className="text-2xl font-bold text-[#312E81]">{systemLanguage === 'zh' ? '词汇复习' : systemLanguage === 'id' ? 'Review Kosakata' : 'Vocabulary Review'}</h2>
              <p className="text-sm text-slate-600">{systemLanguage === 'zh' ? '保留推荐复习，下面查看熟练度不超过 10 的薄弱词汇。' : systemLanguage === 'id' ? 'Ulangi kosakata yang direkomendasikan dan lihat kata dengan kemahiran paling tinggi 10.' : 'Review recommended words and see vocabulary with proficiency at most 10.'}</p></>
          : <><h1 className="text-3xl md:text-4xl font-bold text-[#312E81]">{uiText.pageTitle}</h1>
              <p className="text-[#475569]">{uiText.pageSubtitle}</p></>}

        {/* HSK level access badges */}
        {availableVersions.length > 0 && <nav aria-label="学习体系" className={`flex flex-wrap gap-2 mt-4 ${embedded ? '' : 'justify-center'}`}>
          {availableVersions.map(version => <button key={version} type="button" aria-current={activeVersion === version ? 'page' : undefined}
            onClick={() => { if (version !== activeVersion) { setFilterLevels(embedded ? defaultLevelFilter(version, restrictions) : []); setActiveVersion(version); } }}
            className={`rounded-xl px-5 py-2.5 text-sm font-bold transition-colors ${activeVersion === version ? 'bg-[#4F46E5] text-white' : 'border border-indigo-100 bg-white text-indigo-700 hover:bg-indigo-50'}`}>
            {version === 'CSCA' ? 'CSCA' : `HSK ${version}`}
          </button>)}
        </nav>}
      </div>

      {!embedded && <div className="bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-[#4F46E5] flex items-center justify-center shrink-0">
            <Languages className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#312E81]">{uiText.systemLanguageTitle}</h2>
            <p className="text-sm text-gray-500 mt-1">{uiText.systemLanguageDesc}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {LANGUAGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSystemLanguage(option.value)}
                  className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${
                    systemLanguage === option.value
                      ? 'bg-[#4F46E5] text-white shadow-sm shadow-indigo-500/30'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">{uiText.languageSaved}</p>
          </div>
        </div>
      </div>}

      {/* ── Score Trend ──────────────────────────────────────────────────── */}
      {!embedded && !loadingHistory && trendData.length > 1 && (
        <div className="bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#4F46E5]" />
            <h2 className="text-lg font-bold text-[#312E81]">{uiText.scoreTrend}</h2>
          </div>
          <ScoreTrendChart data={trendData} family={activeVersion === 'CSCA' ? 'CSCA' : 'HSK'} />
        </div>
      )}

      {/* ── Distribution ─────────────────────────────────────────────────── */}
      {!embedded && (loadingVocabStats || (distribution && distribution.total > 0)) && (
        <div className="bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <BarChart2 className="w-5 h-5 text-[#4F46E5]" />
            <h2 className="text-lg font-bold text-[#312E81]">{uiText.distributionTitle}</h2>
          </div>

          {loadingVocabStats ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-[#4F46E5]" />
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-indigo-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-[#312E81]">{totalWords}</div>
                  <div className="text-xs text-gray-500 mt-1 font-medium">{uiText.seenWords}</div>
                </div>
                <div className="bg-red-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{needsWork}</div>
                  <div className="text-xs text-gray-500 mt-1 font-medium">{uiText.needsWork}</div>
                </div>
                <div className="bg-green-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{mastered}</div>
                  <div className="text-xs text-gray-500 mt-1 font-medium">{uiText.mastered}</div>
                </div>
              </div>

              {/* Bars */}
              <div className="space-y-3">
                {tiers.map(tier => {
                  const count = distribution ? distribution[tier.key] : 0;
                  const pct   = (count / maxTierCount) * 100;
                  return (
                    <div key={tier.key} className="flex items-center gap-3">
                      <div className={`w-28 sm:w-36 shrink-0 text-right text-xs font-bold ${tier.text}`}>{tier.label}</div>
                      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${tier.bar} transition-all duration-700`}
                          style={{ width: `${pct}%`, minWidth: count > 0 ? '4px' : '0' }}
                        />
                      </div>
                      <div className={`w-16 shrink-0 text-xs font-bold ${tier.text}`}>{uiText.tierCount(count)}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Recommended Review ───────────────────────────────────────────── */}
      {(loadingVocabStats || recommended.length > 0) && (
        <div className="bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#4F46E5]" />
            <h2 className="text-lg font-bold text-[#312E81]">{uiText.recommendedTitle}</h2>
            {!loadingVocabStats && recommended.length > 0 && (
              <button
                onClick={startReview}
                className="ml-auto px-4 py-2 bg-[#4F46E5] text-white rounded-xl font-bold text-sm hover:bg-indigo-700 active:scale-95 transition-all shadow-sm shadow-indigo-500/30"
              >
                {uiText.startReview}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-5">
            {uiText.recommendedHint}
            {!loadingVocabStats && recommended.length > 0 && uiText.recommendedCount(recommended.length)}
          </p>

          {loadingVocabStats ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-[#4F46E5]" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {recommended.map((item, idx) => {
                const badge    = getProficiencyBadge(item.proficiency ?? 0);
                const accuracy = item.appear > 0 ? Math.round((item.correct / item.appear) * 100) : null;
                return (
                  <div
                    key={`rec-${item.vocab_id}-${idx}`}
                    className="flex flex-col items-center gap-1.5 bg-gray-50 rounded-2xl p-4 border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all"
                  >
                    <div className="self-start text-[10px] font-bold text-gray-300">#{idx + 1}</div>
                    <div className="text-4xl font-bold text-[#312E81] leading-none mt-1">{item.hanzi}</div>
                    <div className="text-xs text-gray-500">{item.pinyin}</div>
                    <div className="text-[11px] text-gray-500 text-center leading-tight line-clamp-2 min-h-[2.2em]">
                      {getMeaning(item, systemLanguage)}
                    </div>
                    <div className="flex items-center gap-1.5 mt-auto pt-1 flex-wrap justify-center">
                      <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold">
                        {vocabLevelLabel(activeVersion, item.level)}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${badge.bg} ${badge.text}`}>
                        {item.proficiency ?? 0}
                      </span>
                    </div>
                    <div className="text-[10px] text-gray-400 text-center">
                      {item.appear ?? 0}{systemLanguage === 'zh' ? '次' : ''}{accuracy !== null && ` · ${accuracy}%`}
                    </div>
                    <div className="text-[10px] text-gray-300">{daysAgo(systemLanguage, item.updated_at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Vocab Table ──────────────────────────────────────────────────── */}
      <div ref={vocabSectionRef} className="bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm scroll-mt-20">
        <div className="flex items-center gap-2 mb-5">
          <BookOpen className="w-5 h-5 text-[#4F46E5]" />
          <h2 className="text-lg font-bold text-[#312E81]">{uiText.allWeakWords}</h2>
          <span className="text-xs font-medium text-gray-500">{uiText.pageSizeLabel(pageSize)}</span>
          {totalVocab > 0 && (
            <span className="ml-auto text-xs font-bold text-[#4F46E5] bg-indigo-50 px-2.5 py-1 rounded-full">
              {uiText.totalWordsBadge(totalVocab)}
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-5">
          {availableLevels.length > 0 && <div>
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{activeVersion === 'CSCA' ? 'CSCA 科目' : uiText.hskLevel}</div>
            <div className="flex flex-wrap gap-2">
              {availableLevels.map(l => (
                <button key={l}
                  onClick={() => setFilterLevels(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l])}
                  aria-pressed={filterLevels.includes(l)}
                  className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all ${
                    filterLevels.includes(l) ? 'bg-[#4F46E5] text-white shadow-sm' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {vocabLevelLabel(activeVersion, l)}
                </button>
              ))}
            </div>
          </div>}
        </div>

        {loadingVocab ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-[#4F46E5]" />
          </div>
        ) : vocabError ? (
          <div aria-live="polite" className="flex flex-col items-center gap-3 py-12 text-center text-gray-600">
            <p>{uiText.vocabLoadError}</p>
            <button type="button" onClick={() => void loadVocab(currentPage)} className="rounded-xl border border-indigo-200 px-4 py-2 font-semibold text-indigo-700 hover:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-500">
              {uiText.retry}
            </button>
          </div>
        ) : hasProficiencyData === false || vocab.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <BookOpen className="w-14 h-14 text-gray-200 mb-4" />
            <p className="text-gray-500 font-medium">
              {hasProficiencyData === false
                ? uiText.noVocabRecords
                : uiText.noVocabForFilter}
            </p>
          </div>
        ) : (
          <>
            {renderVocabPagination('top')}
            <div className="space-y-3 md:hidden">
              {vocab.map((item, idx) => {
                const badge = getProficiencyBadge(item.proficiency ?? 0);
                const accuracy = item.appear > 0 ? Math.round((item.correct / item.appear) * 100) : null;
                return (
                  <article key={`mobile-${item.vocab_id}-${idx}`} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <strong className="text-xl text-[#312E81] break-words">{item.hanzi}</strong>
                        <span className="ml-2 text-sm text-gray-500">{item.pinyin}</span>
                      </div>
                      <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-700">{vocabLevelLabel(activeVersion, item.level)}</span>
                    </div>
                    <p className="mt-2 break-words text-sm text-gray-600">{getMeaning(item, systemLanguage)}</p>
                    <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-gray-200 pt-3 text-center text-xs">
                      <div><dt className="text-gray-500">{uiText.proficiency}</dt><dd className={`mt-1 font-bold ${badge.text}`}>{item.proficiency ?? 0}</dd></div>
                      <div><dt className="text-gray-500">{uiText.appear}</dt><dd className="mt-1 font-bold text-gray-700">{item.appear ?? 0}</dd></div>
                      <div><dt className="text-gray-500">{uiText.correctRate}</dt><dd className="mt-1 font-bold text-gray-700">{accuracy === null ? '—' : `${accuracy}%`}</dd></div>
                    </dl>
                  </article>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase tracking-wider">{uiText.hanzi}</th>
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase tracking-wider">{uiText.pinyin}</th>
                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-500 uppercase tracking-wider">{uiText.meaning}</th>
                    <th className="text-center py-2 px-3 text-xs font-bold text-gray-500 uppercase tracking-wider">{uiText.level}</th>
                    <th className="text-center py-2 px-3 text-xs font-bold uppercase tracking-wider" aria-sort={sortCol === 'proficiency' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'}>
                      <button type="button" onClick={() => toggleSort('proficiency')} className="rounded px-1 hover:text-[#4F46E5] focus-visible:ring-2 focus-visible:ring-indigo-500">
                        {uiText.proficiency}<SortIcon active={sortCol === 'proficiency'} dir={sortDir} />
                      </button>
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-bold uppercase tracking-wider" aria-sort={sortCol === 'appear' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'}>
                      <button type="button" onClick={() => toggleSort('appear')} className="rounded px-1 hover:text-[#4F46E5] focus-visible:ring-2 focus-visible:ring-indigo-500">
                        {uiText.appear}<SortIcon active={sortCol === 'appear'} dir={sortDir} />
                      </button>
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-bold uppercase tracking-wider" aria-sort={sortCol === 'correct_rate' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'}>
                      <button type="button" onClick={() => toggleSort('correct_rate')} className="rounded px-1 hover:text-[#4F46E5] focus-visible:ring-2 focus-visible:ring-indigo-500">
                        {uiText.correctRate}<SortIcon active={sortCol === 'correct_rate'} dir={sortDir} />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vocab.map((item, idx) => {
                    const badge    = getProficiencyBadge(item.proficiency ?? 0);
                    const accuracy = item.appear > 0 ? Math.round((item.correct / item.appear) * 100) : null;
                    return (
                      <tr key={`${item.vocab_id}-${idx}`} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-3 font-bold text-[#312E81] text-base">{item.hanzi}</td>
                        <td className="py-3 px-3 text-gray-500">{item.pinyin}</td>
                        <td className="py-3 px-3 text-gray-600 max-w-[200px] truncate" title={getMeaning(item, systemLanguage)}>{getMeaning(item, systemLanguage)}</td>
                        <td className="py-3 px-3 text-center">
                          <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold">{vocabLevelLabel(activeVersion, item.level)}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badge.bg} ${badge.text}`}>
                            {item.proficiency ?? 0}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center text-gray-600">{item.appear ?? 0}</td>
                        <td className="py-3 px-3 text-center">
                          {accuracy !== null
                            ? <span className={accuracy >= 60 ? 'text-green-600 font-bold' : 'text-red-500 font-bold'}>{accuracy}%</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {renderVocabPagination('bottom')}
          </>
        )}
      </div>
    </div>
  );
};

export default MyVocab;
